import { inject, Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { switchMap, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { MoldContainerService } from '../../services/container.service';
import { AlertService } from '../../../../core/services/alert.service';
import { MoldFormApiService } from '../../api/form.service';
import { MoldRevisionItem } from '../../models/item';
import { MoldAuthService } from '../../services/auth.service';
import {
  MoldFormDialogComponent,
  MoldFormDialogTypes,
} from '../../dialog/form/form.component';
import { MoldRevision } from '../../../../api/elite/types/mold-revision';
import { SingleDepartmentService } from '../../../../shared/services/single-department.service';

@Injectable()
export class MoldFormService<Type extends MoldRevisionItem> {
  protected readonly dialog = inject(MatDialog);
  protected readonly containerService = inject(MoldContainerService);
  protected readonly authService = inject(MoldAuthService);
  protected readonly alertService = inject(AlertService);
  protected readonly apiRouter = inject(MoldFormApiService);
  protected readonly departmentService = inject(SingleDepartmentService);

  createMold(): void {
    if (!this.authService.add()) {
      this.alertService.error('No tienes permisos para agregar moldes');
      return;
    }
    let departmentId: number;
    // We are only doing it to the active list, because when we create a new mold
    // this one should be always active at the beginning
    this.containerService.molds$
      .pipe(
        take(1),
        switchMap((molds) => {
          departmentId = molds[0]?.departmentId;
          return this.createMoldDialog(molds);
        }),
        switchMap((res: MoldFormDialogTypes['return']) =>
          this.createMoldObservable(res, departmentId)
        )
      )
      .subscribe((result) => {
        if (result) {
          const newMold = MoldRevisionItem.fromObject(result);
          this.containerService.newMoldRevision(newMold as any);
          this.alertService.success('Molde creado');
        }
      });
  }

  protected createMoldDialog(molds: Type['mold'][]): Observable<any> {
    return this.dialog
      .open(MoldFormDialogComponent, {
        data: {
          molds,
          type: 'create',
          department: this.departmentService.NAME,
        },
      })
      .afterClosed();
  }

  protected createMoldObservable(
    res: MoldFormDialogTypes['return'],
    departmentId: number
  ): Observable<MoldRevision | undefined> {
    if (!res) {
      return of(undefined);
    }
    return this.apiRouter.createMold({
      ...res,
      moldData: {
        ...res.moldData,
        departmentId,
      },
    });
  }
}
